import dashboardSalesService from "@services/dashboard/dashboardSalesService"
// Hai widget W-SAL-05/06 gọi endpoint CŨ của `DashboardAppService` (task 12 chỉ mở rộng, không tạo endpoint
// mới) nên dùng lại đúng service đã có của dashboard cũ thay vì nhân bản hai method.
import legacyDashboardService from "@services/report/dashboardService"
import {
  toLegacyDashboardPayload,
} from "@scenes/dashboardSales/widgets/business/legacyDashboardPayload"
// `import type` là bắt buộc: interface được dùng trong chữ ký có decorator, và với
// isolatedModules + emitDecoratorMetadata thì import thường sẽ lỗi TS1272 (bẫy 23).
import type {
  IApprovalQueue,
  IChannelFunnel,
  IBookingTrend,
  IFunnel,
  IPendingPricing,
  ILegacyAgencyPerformanceRow,
  ILegacyUnitStatusRow,
  ISaleEventPerf,
  IContractPipeline,
  IExpiringBookings,
  IScheduleSetup,
} from "@services/dashboard/dashboardSalesService"
import { action, makeAutoObservable, observable } from "mobx"

/**
 * Store của Dashboard Sales Admin (Feature-0199). Mỗi widget có bộ ba `data*` / `loading*` / `error*`
 * riêng: dashboard load song song và một widget lỗi KHÔNG được kéo sập các widget khác — giống
 * `DashboardCsStore` và `DashboardFinanceStore`.
 */
class DashboardSalesStore {
  @observable dataExpiringBookings?: IExpiringBookings
  @observable loadingExpiringBookings = false
  @observable errorExpiringBookings = false

  @observable dataContractPipeline?: IContractPipeline
  @observable loadingContractPipeline = false
  @observable errorContractPipeline = false

  @observable dataFunnel?: IFunnel
  @observable loadingFunnel = false
  @observable errorFunnel = false

  @observable dataChannelFunnel?: IChannelFunnel[]
  @observable loadingChannelFunnel = false
  @observable errorChannelFunnel = false

  @observable dataPendingPricing?: IPendingPricing
  @observable loadingPendingPricing = false
  @observable errorPendingPricing = false

  @observable dataUnitInventory: ILegacyUnitStatusRow[] = []
  @observable loadingUnitInventory = false
  @observable errorUnitInventory = false

  @observable dataChannelPerformance: ILegacyAgencyPerformanceRow[] = []
  @observable loadingChannelPerformance = false
  @observable errorChannelPerformance = false

  @observable dataBookingTrend?: IBookingTrend
  @observable loadingBookingTrend = false
  @observable errorBookingTrend = false

  @observable dataSaleEventPerf?: ISaleEventPerf[]
  @observable loadingSaleEventPerf = false
  @observable errorSaleEventPerf = false

  @observable dataScheduleSetup?: IScheduleSetup
  @observable loadingScheduleSetup = false
  @observable errorScheduleSetup = false

  @observable dataApprovalQueue?: IApprovalQueue
  @observable loadingApprovalQueue = false
  @observable errorApprovalQueue = false

  constructor() {
    makeAutoObservable(this)
  }

  @action
  getExpiringBookings = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingExpiringBookings = true
    this.errorExpiringBookings = false
    try {
      this.dataExpiringBookings =
        await dashboardSalesService.getExpiringBookings(params)
    } catch {
      this.errorExpiringBookings = true
    } finally {
      this.loadingExpiringBookings = false
    }
  }

  @action
  getContractPipeline = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingContractPipeline = true
    this.errorContractPipeline = false
    try {
      this.dataContractPipeline =
        await dashboardSalesService.getContractPipeline(params)
    } catch {
      this.errorContractPipeline = true
    } finally {
      this.loadingContractPipeline = false
    }
  }

  @action
  getPendingPricingApproval = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingPendingPricing = true
    this.errorPendingPricing = false
    try {
      this.dataPendingPricing =
        await dashboardSalesService.getPendingPricingApproval(params)
    } catch {
      this.errorPendingPricing = true
    } finally {
      this.loadingPendingPricing = false
    }
  }

  /**
   * Widget W-SAL-05. Endpoint cũ trả `PagedResultDto`, và chỉ nhận MỘT `ProjectId` — phần quy đổi shape nằm
   * ở `toLegacyDashboardPayload` (có test).
   */
  @action
  getUnitInventory = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingUnitInventory = true
    this.errorUnitInventory = false
    try {
      const result = await legacyDashboardService.getSummaryUnitStatusWithProject(
        toLegacyDashboardPayload(params, { maxResultCount: 20 })
      )
      this.dataUnitInventory = result?.items ?? []
    } catch {
      this.errorUnitInventory = true
    } finally {
      this.loadingUnitInventory = false
    }
  }

  /** Widget W-SAL-06. `agencyCountType`: 1 booking · 2 giá trị booking · 3 hợp đồng · 4 giá trị hợp đồng. */
  @action
  getChannelPerformance = async (
    params?: Record<string, unknown>,
    agencyCountType = 1
  ): Promise<void> => {
    this.loadingChannelPerformance = true
    this.errorChannelPerformance = false
    try {
      this.dataChannelPerformance =
        (await legacyDashboardService.getSummaryAgencyMasterPerformance({
          ...toLegacyDashboardPayload(params),
          AgencyCountType: agencyCountType,
        })) ?? []
    } catch {
      this.errorChannelPerformance = true
    } finally {
      this.loadingChannelPerformance = false
    }
  }

  @action
  getBookingTrend = async (params?: Record<string, unknown>): Promise<void> => {
    this.loadingBookingTrend = true
    this.errorBookingTrend = false
    try {
      this.dataBookingTrend = await dashboardSalesService.getBookingTrend(params)
    } catch {
      this.errorBookingTrend = true
    } finally {
      this.loadingBookingTrend = false
    }
  }

  @action
  getSaleEventPerformance = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingSaleEventPerf = true
    this.errorSaleEventPerf = false
    try {
      this.dataSaleEventPerf =
        await dashboardSalesService.getSaleEventPerformance(params)
    } catch {
      this.errorSaleEventPerf = true
    } finally {
      this.loadingSaleEventPerf = false
    }
  }

  @action
  getFunnel = async (params?: Record<string, unknown>): Promise<void> => {
    this.loadingFunnel = true
    this.errorFunnel = false
    try {
      this.dataFunnel = await dashboardSalesService.getFunnel(params)
    } catch {
      this.errorFunnel = true
    } finally {
      this.loadingFunnel = false
    }
  }

  @action
  getFunnelByChannel = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingChannelFunnel = true
    this.errorChannelFunnel = false
    try {
      this.dataChannelFunnel =
        await dashboardSalesService.getFunnelByChannel(params)
    } catch {
      this.errorChannelFunnel = true
    } finally {
      this.loadingChannelFunnel = false
    }
  }

  @action
  getPaymentScheduleSetup = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingScheduleSetup = true
    this.errorScheduleSetup = false
    try {
      this.dataScheduleSetup =
        await dashboardSalesService.getPaymentScheduleSetup(params)
    } catch {
      this.errorScheduleSetup = true
    } finally {
      this.loadingScheduleSetup = false
    }
  }

  @action
  getApprovalQueue = async (
    params?: Record<string, unknown>
  ): Promise<void> => {
    this.loadingApprovalQueue = true
    this.errorApprovalQueue = false
    try {
      this.dataApprovalQueue =
        await dashboardSalesService.getApprovalQueue(params)
    } catch {
      // Nuốt lỗi có chủ ý: WidgetShell hiển thị trạng thái lỗi + nút thử lại của RIÊNG widget này.
      this.errorApprovalQueue = true
    } finally {
      this.loadingApprovalQueue = false
    }
  }
}

export default DashboardSalesStore
